import { call, put, select, all } from 'redux-saga/effects';
import * as actions from './actions';
import { produce } from 'immer';
import axios from 'axios';
import { sagas as eventSagas } from 'store/event';
import { waitFor } from 'store/helpers';
import { Base64 } from 'js-base64';
import { getParams } from 'store/helpers/helperFunctions';
import moment from 'moment';
import { updateSurveyFilters, updateIndividualSurveyResultFilters } from './actions';

const { waitForGetSupportedEventTypes } = eventSagas;

export function* waitForGetSurveys() {
  return yield waitFor(
    actions.getSurveys(),
    actions.GETTING_SURVEYS_SUCCEEDED,
    actions.GETTING_SURVEYS_FAILED
  );
}

export function* waitForSaveSurveyLogo() {
  return yield waitFor(
    actions.saveSurveyLogo(),
    actions.SAVING_SURVEY_LOGO_SUCCEEDED,
    actions.SAVING_SURVEY_LOGO_FAILED
  );
}

// the actual http calls
const apiRoot = (window._env_ && window._env_.REACT_APP_SURVEY_API_PATH) || '/';

const getSurveyApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}survey/admin/tenants/${tenantId}/context/${businessContextId}`;

const getAdmSurveyApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}adm-survey/tenants/${tenantId}/context/${businessContextId}`;

const getSurveyResultsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}adm-survey/results/tenants/${tenantId}/context/${businessContextId}`;

const getContentServiceApiRoot = (tenantId, businessContextId) =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/content-service/content/tenants/${tenantId}/properties/${businessContextId}/contents`;

const getAssociatedCampaignsApiRoot = (tenantId, businessContextId) =>
  `${window._env_?.REACT_APP_API_PATH}tenants/${tenantId}/context/${businessContextId}/campaign`;

const getEventDetailsApiRoot = (tenantId, businessContextId) =>
  `${window._env_?.REACT_APP_API_PATH}tenants/${tenantId}/context/${businessContextId}/event-report`;

const getProfileDetailsApiRoot = (tenantId, businessContextId) =>
  `${window._env_?.REACT_APP_LIST_API_PATH}profile/tenants/${tenantId}/context/${businessContextId}`;

let isArchiveInProgress = false;

export const getSurveysAsync = (tenantId, businessContextId, surveyId) =>
  axios
    .get(
      `${surveyId ? getAdmSurveyApiRoot(tenantId, businessContextId) : getSurveyApiRoot(tenantId, businessContextId)}${
        surveyId ? `/${surveyId}` : '/all/get?index=0&pageSize=999'
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getPaginatedSurveysAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams,
  forEvents
) => {
  return axios
    .post(
      `${getAdmSurveyApiRoot(tenantId, businessContextId)}/search${forEvents ? `` : `?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getMinimalSurveysAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getAdmSurveyApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getSurveyResultsAsync = (
  tenantId,
  businessContextId,
  surveyHash,
  queryParams
) =>
  axios
    .get(
      `${getSurveyResultsApiRoot(tenantId, businessContextId)}/${surveyHash}${queryParams ? `?${queryParams}` : ``}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getIndividualSurveyResultsAsync = (
  tenantId,
  businessContextId,
  surveyHash,
  index,
  pageSize,
  filterBy,
  sortBy,
  orderBy,
  campaignId
) =>
  axios
    .get(
      `${getSurveyResultsApiRoot(
        tenantId,
        businessContextId
      )}/${surveyHash}?index=${index}&pageSize=${pageSize}${
        typeof filterBy === 'boolean' ? `&acknowledged=${filterBy}` : ``
      }${sortBy ? `&sortBy=${sortBy}` : ``}${
        orderBy ? `&orderBy=${orderBy}` : ``
      }${campaignId.length !== 0 ? `&campaignId=${campaignId[0]}` : ``}`
    )
    .then(response => ({
      data: response.data,
      totalCount: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const getIndividualSurveyResultsSearchAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams,
  forEvents
) =>
  axios
    .post(
      `${getSurveyResultsApiRoot(tenantId, businessContextId)}/search${forEvents ? `` : `?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalCount: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });

export const createSurveyAsync = (tenantId, businessContextId, survey) =>
  axios
    .post(`${getAdmSurveyApiRoot(tenantId, businessContextId)}`, survey)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateSurveyAsync = (tenantId, businessContextId, survey) =>
  axios
    .put(
      `${getAdmSurveyApiRoot(tenantId, businessContextId)}/${survey.id}`,
      survey
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteSurveyAsync = (tenantId, businessContextId, surveyId) =>
  axios
    .delete(`${getSurveyApiRoot(tenantId, businessContextId)}/${surveyId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const saveSurveyLogoAsync = (tenantId, businessContextId, formData) =>
  axios
    .post(
      `${getContentServiceApiRoot(tenantId, businessContextId)}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const sendResponseAsync = (tenantId, businessContextId, payload) =>
  axios
    .post(
      `${getSurveyResultsApiRoot(tenantId, businessContextId)}/response`,
      payload
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getAssociatedCampaignUuidAsync = (
  tenantId,
  businessContextId,
  surveyHash
) =>
  axios
    .get(
      `${getSurveyResultsApiRoot(
        tenantId,
        businessContextId
      )}/campaigns/surveyHash/${surveyHash}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getAssociatedCampaignDetailsAsync = (
  tenantId,
  businessContextId,
  campaignUuidList
) =>
  axios
    .post(
      `${getAssociatedCampaignsApiRoot(tenantId, businessContextId)}/uuid/bulk`,
      campaignUuidList
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getGuestDetailsAsync = (tenantId, businessContextId, eventId) =>
  axios
    .get(`${getEventDetailsApiRoot(tenantId, businessContextId)}/${eventId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getProfileDetailsAsync = (tenantId, businessContextId, profileUuid, subscriberListId) =>
  axios
    .get(`${getProfileDetailsApiRoot(tenantId, businessContextId)}/profileId/${profileUuid}?listId=${subscriberListId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getSurveyResultsCsvFileAsync = (
  tenantId, 
  businessContextId,
  payload
) =>
  axios({
    url: `${getSurveyResultsApiRoot(tenantId, businessContextId)}/export`,
    method: 'POST',
    responseType: 'blob',
    data: payload
  })
  .then(response => response.data)
    .catch(err => {
      throw err;
    });

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentSurvey = store => (store.survey ? store.survey.current : {});
const surveyList = store => (store.survey ? store.survey.list : []);
const paginatedSurveyList = store =>
  store.survey ? store.survey.paginatedList : [];
const surveyHash = store =>
  store.survey ? store.survey.surveyHashFilter : [];
const eventId = store =>
  store.survey ? store.survey.eventId : null;
const selectedCampaignUuid = store =>
  store.survey ? store.survey.campaignUuid : null;
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const pageNo = store =>
  store.survey.filters ? store.survey.filters?.pageNo : 0;
const searchInput = store =>
  store.survey.filters ? store.survey.filters?.searchInput : '';
const sortBy = store =>
  store.survey.filters ? store.survey.filters?.sortBy : 'name';
const sortOrder = store =>
  store.survey.filters ? store.survey.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.survey.filters ? store.survey.filters?.rowsPerPage : 10;
const displayActive = store =>
  store.survey.filters ? store.survey.filters?.displayActive : true;
const filterTemplates = store =>
  store.survey.filters ? store.survey.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.survey.filters ? store.survey.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.survey.filters ? store.survey.filters?.filterProductCategories : [];
const filterTags = store =>
  store.survey.surveyResultFilters ? store.survey.surveyResultFilters?.filterTags : [];
const totalCount = store =>
  store.survey.filters ? store.survey.filters?.totalCount : 0;
const associatedCampaignList = store =>
  store.survey ? store.survey.associatedCampaignList : [];
const guestDetails = store => (store.survey ? store.survey.guestDetails : {});
const filterStartDate = store =>
  store.survey.surveyResultFilters ? store.survey?.surveyResultFilters?.filterStartDate : null;
const filterEndDate = store =>
  store.survey.surveyResultFilters ? store.survey.surveyResultFilters?.filterEndDate : null;
const filterSurveyQuestions = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.filterSurveyQuestions : null;
const individualResultSortBy = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.sortBy : 'name';
const individualResultSortOrder = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.sortOrder : 'ASC';
const individualResultPageNo = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.pageNo : 0;
const individualResulRowsPerPage = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.rowsPerPage : 10;
const individualResulFilterTypes = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.filterTypes : [];
const individualResulFilterTags = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters?.filterTags : [];
const individualResulFilterStartDate = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters.filterStartDate : null;
const individualResulFilterEndDate = store =>
  store.survey.individualSurveyResultFilters ? store.survey.individualSurveyResultFilters.filterEndDate : null;

// helper saga to prevent culling the saga tree on rejection of a parallelized task.
function* reflect(callArgs) {
  try {
    return { value: yield call(...callArgs), status: 'resolved' };
  } catch (ex) {
    return ex;
  }
}

const addChoices = survey => {
  let tempSurvey = { ...survey };

  //find questions
  let details = JSON.parse(tempSurvey?.details)?.pages;
  let tempQuestions = [];
  details.map(q => {
    if (q?.elements) tempQuestions = [...tempQuestions, ...q?.elements];
  });

  //add choices and add conditionValueText(backwardcompatibility)
  tempSurvey.surveyQuestionResponseRules = tempSurvey.surveyQuestionResponseRules.map(
    r => {
      let q = tempQuestions.find(q => r.surveyQuestionName === q.name);
      let value = (q?.choices || q?.columns)?.filter(
        opt => opt.value === r.conditionData.conditionValue
      );
      value =
        typeof value?.[0] === 'object' ? value?.[0]?.['text'] : value?.[0];
      value = value || r.conditionData.conditionValue;
      r = {
        ...r,
        choices: q?.choices || q?.columns || [],
        subQuestionOptions: q?.rows || [],
        conditionData: {
          ...r.conditionData,
          conditionValueText: value
        }
      };
      return r;
    }
  );

  return tempSurvey;
};

const getSurveyResponsePayload = surveyResponses => {
  let convertedPayload = {};
  surveyResponses
    .filter(response => response.checked)
    .forEach(response => {
      if (response.type === 'checkbox') {
        if (response.regex && response.regex.trim().length > 0) {
          convertedPayload[response.name] = new Array(response.regex.trim());
        }
      } else if (response.type === 'rating') {
        if (response.regex && response.regex.trim().length > 0) {
          convertedPayload[response.name] = parseInt(response.regex.trim()) == response.regex.trim() ? parseInt(response.regex.trim()) : response.regex.trim();
        }
      } else if (response.type === 'matrix') {
        let tempObj = {};
        response.rows.forEach(value => {
          if (value.regex && value.regex.trim().length > 0) {
            tempObj[(typeof value.name === 'string') ? value.name : value.name.text] = value.regex.trim();
          }
        });
        if (Object.keys(tempObj).length > 0) {
          convertedPayload[response.name] = tempObj;
        }
      } else {
        if (response.regex && response.regex.trim().length > 0) {
          convertedPayload[response.name] = response.regex.trim();
        }
      }
    });
  return convertedPayload;
}

export function* getSurveysIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(surveyList);
    if (
      action.type === actions.GET_SURVEYS &&
      (currentList.length === 0 || payload.force === true)
    ) {
      yield put(actions.gettingSurveys());
      currentList = yield call(getSurveysAsync, tenantId, businessContextId);
    }
    currentList = currentList.map(survey => {
      if (
        survey?.surveyQuestionResponseRules?.length > 0 &&
        !survey?._hasSetChoices
      ) {
        survey = addChoices(survey);
      }
      if (!survey.hasOwnProperty('_hasSetChoices')) {
        survey = { ...survey, _hasSetChoices: true };
      }
      return survey;
    });

    yield put(actions.gettingSurveysSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingSurveysFailed(ex));
  }
}

export function* getPaginatedSurveysIfNeeded(action) {
  const { payload = {} } = action;
  const { forEvents } = payload;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedSurveyList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);
    let filteredSurveyHash = yield select(surveyHash);
    let filteredEventId = yield select(eventId);
    let filteredCamapaignUuid = yield select(selectedCampaignUuid);

    if (
      action.type === actions.GET_PAGINATED_SURVEYS &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        active: yield select(displayActive),
        searchScopes: yield select(filterTemplates),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        ...((forEvents && (filteredSurveyHash.length > 0)) && { surveyHash: filteredSurveyHash })
      };

      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updateSurveyFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedSurveys());
      currentList = yield call(
        getPaginatedSurveysAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams,
        forEvents
      );
      if (!forEvents) {
        yield put(updateSurveyFilters({ totalCount: currentList.totalRecords }));
      }
    }
    currentList.data = currentList.data.map(survey => {
      if (
        survey?.surveyQuestionResponseRules?.length > 0 &&
        !survey?._hasSetChoices
      ) {
        survey = addChoices(survey);
      }
      if (!survey.hasOwnProperty('_hasSetChoices')) {
        survey = { ...survey, _hasSetChoices: true };
      }
      return survey;
    });
    if (forEvents) {
      let surveyHashArray = { '' : [...new Set(currentList.data.map(r => {
        return {
          surveyHash: r.surveyHash,
          value: r.id,
          label: r.name,
          link: `/gcm/surveys/results/${r.id}/${r.surveyHash}/response${filteredEventId ? `/${filteredEventId}` : ``}${filteredCamapaignUuid ? `/${filteredCamapaignUuid}` : ``}`
        }
      }))]};
      yield put(actions.updateSurveyResultsDetails(surveyHashArray))
    } else {
      isArchiveInProgress = false;
      yield put(actions.gettingPaginatedSurveysSucceeded(currentList));
    };
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(actions.updateSurveyResultsDetails({}))
    yield put(updateSurveyFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedSurveysFailed(ex));
  }
}

export function* getMinimalSurveysIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    
    if (action.type === actions.GET_MINIMAL_SURVEYS) {
      yield put(actions.gettingMinimalSurveys());
      
      let urlParams = {
        index: payload.index || 0,
        pageSize: payload.pageSize || 100
      };
      
      let bodyParams = {
        active: payload.active !== undefined ? payload.active : true,
        ...(payload.productCategory && { productCategory: payload.productCategory }),
        ...(payload.productName && { productName: payload.productName }),
        ...(payload.eventTypeCategory && { eventTypeCategory: payload.eventTypeCategory }),
        ...(payload.name && { name: payload.name })
      };
      
      let currentList = yield call(
        getMinimalSurveysAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      
      yield put(actions.gettingMinimalSurveysSucceeded({
        data: currentList.data,
        totalRecords: currentList.totalRecords
      }));
    }
  } catch (ex) {
    yield put(actions.gettingMinimalSurveysFailed(ex));
  }
}

export function* getSurveyIfNeeded(action) {
  const { payload = {} } = action;
  try {
    yield put(actions.gettingSurvey());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let survey = yield select(currentSurvey);
    let surveyId = payload.surveyId;

    if (survey.id !== surveyId || payload.force === true) {
      survey = yield call(
        getSurveysAsync,
        tenantId,
        businessContextId,
        surveyId
      );
    }

    if (
      survey?.surveyQuestionResponseRules?.length > 0 &&
      !survey?._hasSetChoices
    ) {
      survey = addChoices(survey);
    }

    if (!survey.hasOwnProperty('_hasSetChoices')) {
      survey = { ...survey, _hasSetChoices: true };
    }

    yield put(actions.gettingSurveySucceeded({ survey }));
  } catch (ex) {
    yield put(actions.gettingSurveyFailed(ex));
  }
}

export function* createSurveyFromCurrent() {
  try {
    yield put(actions.creatingSurvey());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let survey = yield select(currentSurvey);
    let image;
    let questions;
    if (!survey.id) {
      survey = produce(survey, draftSurvey => {
        draftSurvey.tenantId = tenantId;
        draftSurvey.businessContextId = businessContextId;
      });

      if (survey.file) {
        ({ image } = yield all({
          image: yield call(waitForSaveSurveyLogo, survey)
        }));
        survey = produce(survey, draftSurvey => {
          draftSurvey.preview = undefined;
          draftSurvey.file = undefined;
          draftSurvey.surveyTheme = {
            ...draftSurvey.surveyTheme,
            imageId: image.savedFile.id
          };
        });
      }
      questions = survey.questions;
      survey = yield call(
        createSurveyAsync,
        tenantId,
        businessContextId,
        survey
      );
    }
    if (
      survey?.surveyQuestionResponseRules?.length > 0 &&
      !survey?._hasSetChoices
    ) {
      survey = addChoices(survey);
    }

    if (!survey.hasOwnProperty('_hasSetChoices')) {
      survey = { ...survey, _hasSetChoices: true };
    }
    yield put(
      actions.creatingSurveySucceeded({ ...survey, questions: questions })
    );
  } catch (ex) {
    yield put(actions.creatingSurveyFailed(ex));
  }
}

export function* updateSurveyFromCurrent() {
  try {
    yield put(actions.updatingSurvey());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let survey = yield select(currentSurvey);

    let image;
    if (survey.file) {
      ({ image } = yield all({
        image: yield call(waitForSaveSurveyLogo, survey)
      }));
      survey = produce(survey, draftSurvey => {
        draftSurvey.preview = undefined;
        draftSurvey.file = undefined;
        draftSurvey.surveyTheme = {
          ...draftSurvey.surveyTheme,
          imageId: image.savedFile.id
        };
      });
    }
    let questions = survey.questions;
    if (survey.id) {
      survey = yield call(
        updateSurveyAsync,
        tenantId,
        businessContextId,
        survey
      );
    }
    if (
      survey?.surveyQuestionResponseRules?.length > 0 &&
      !survey?._hasSetChoices
    ) {
      survey = addChoices(survey);
    }

    if (!survey.hasOwnProperty('_hasSetChoices')) {
      survey = { ...survey, _hasSetChoices: true };
    }
    yield put(
      actions.updatingSurveySucceeded({ ...survey, questions: questions })
    );
  } catch (ex) {
    yield put(actions.updatingSurveyFailed(ex));
  }
}

export function* deleteSurveyByIdList(action) {
  const { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let surveyIds = payload.surveyIds;
    let surveys = yield select(paginatedSurveyList);

    let deletedSurveyStatuses = [];
    if (surveyIds.length > 0) {
      yield put(actions.deletingSurveys());
      // all() prallelizes delete operations and waits until they all finish.
      // if the delete template API method can be made to return the deleted id this could be set to a variable and report partial success which would make for better failure handling.
      if (payload.hardDelete) {
        deletedSurveyStatuses = yield all(
          surveyIds
            .map(surveyId => [
              deleteSurveyAsync,
              tenantId,
              businessContextId,
              surveyId
            ])
            .map(reflect)
        );
      } else {
        deletedSurveyStatuses = yield all(
          surveyIds
            .map(surveyId => {
              const survey = surveys.find(t => t.id === surveyId);
              return [
                updateSurveyAsync,
                tenantId,
                businessContextId,
                {
                  ...survey,
                  active: !payload.activeStatus
                }
              ];
            })
            .map(reflect)
        );
      }
    }
    if (
      deletedSurveyStatuses.filter(status => status.status === 'resolved')
        .length === surveyIds.length
    ) {
      isArchiveInProgress = true;
      yield all([
        put(actions.deletingSurveysSucceeded(surveyIds, payload.hardDelete)),
        put(actions.getPaginatedSurveys({ force: true }))
      ]);
    } else {
      yield put(
        actions.deletingSurveysFailed({
          message: 'Not all requested templates could be deleted.',
          deletedSurveyStatuses
        })
      );
    }
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.deletingSurveysFailed({
        ...ex,
        hardDelete: payload.hardDelete
      })
    );
  }
}
export function* getSurveyResultsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    yield put(actions.gettingSurveyResults());
    const params = new URLSearchParams();  
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let campaignUuid = yield select(filterTags);
    let startDate = yield select(filterStartDate);
    let endDate = yield select(filterEndDate);
    let surveyResults;
    let surveyHash = payload.surveyHash;
    if (campaignUuid.length > 0) {  
      params.append('campaignUuid', campaignUuid);  
    }  
    if (startDate && moment(startDate).startOf('day').isSameOrBefore(moment().startOf('day'))) {
      params.append('startDate', startDate);
    }
    if (endDate && moment(endDate).startOf('day').isSameOrBefore(moment().startOf('day'))
      && (startDate ? moment(endDate).startOf('day').isSameOrAfter(moment(startDate).startOf('day')) : true)) {
      params.append('endDate', endDate);
    }  
    const queryString = params.toString(); 

    surveyResults = yield call(
      getSurveyResultsAsync,
      tenantId,
      businessContextId,
      surveyHash,
      queryString
    );
    surveyResults = surveyResults.filter(r => {
      return r.businessContextId == businessContextId;
    });
    let groupedResults = surveyResults.map(r => JSON.parse(r.results));
    surveyResults = {
      ...surveyResults[0],
      results: groupedResults
    };

    yield put(actions.gettingSurveyResultsSucceeded({ surveyResults }));
  } catch (ex) {
    yield put(actions.gettingSurveyResultsFailed(ex));
  }
}

export function* getIndividualSurveyResultsIfNeeded(action) {
  const { surveyHash, forEvents } = action.payload || {};
  try {
    yield put(actions.gettingIndividualSurveyResults());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);

    let urlParams = {
      index: yield select(individualResultPageNo),
      pageSize: yield select(individualResulRowsPerPage)
    };

    let acknowledged = yield select(individualResulFilterTypes);
    let campaignUuid = yield select(individualResulFilterTags);
    let startDate = yield select(individualResulFilterStartDate);
    let endDate = yield select(individualResulFilterEndDate);
    let sort = yield select(individualResultSortBy);
    let surveyQuestions = yield select(filterSurveyQuestions);
    let filteredEventId = yield select(eventId);
    let filteredCamapaignUuid = yield select(selectedCampaignUuid);
    if (acknowledged.length == 0 || acknowledged.length == 2) {
      acknowledged = undefined;
    } else if (acknowledged[0] == 'ACKNOWLEDGED') {
      acknowledged = true;
    } else {
      acknowledged = false;
    }
    let bodyParams = {
      surveyHash: surveyHash,
      ...(sort != '' && {
        sortBy: yield select(individualResultSortBy),
        sortOrder: yield select(individualResultSortOrder)
      }),
      ...(typeof acknowledged === 'boolean' && { acknowledged: acknowledged }),
      ...(campaignUuid.length > 0 && { campaignUuid: campaignUuid }),
      ...(Object.keys(getSurveyResponsePayload(surveyQuestions)).length > 0 && { surveyResponses: getSurveyResponsePayload(surveyQuestions) }),
      ...(startDate && moment(startDate).startOf('day').isSameOrBefore(moment().startOf('day')) && { startDate: startDate }),
      ...(endDate && moment(endDate).startOf('day').isSameOrBefore(moment().startOf('day'))
      && (startDate ? moment(endDate).startOf('day').isSameOrAfter(moment(startDate).startOf('day')) : true) && { endDate: endDate }),
      ...(filteredEventId && { eventId: filteredEventId }),
      ...((forEvents && filteredCamapaignUuid) && { campaignUuid: [filteredCamapaignUuid] })
    };
    let surveyResults = yield call(
      getIndividualSurveyResultsSearchAsync,
      tenantId,
      businessContextId,
      urlParams,
      bodyParams,
      forEvents
    );
    if (forEvents) {
      let surveyHashArray = [...new Set(surveyResults.data.map(r => r.surveyHash))];
      yield put(actions.updateSurveyHashFilter(surveyHashArray))
    } else {
      yield put(updateIndividualSurveyResultFilters({ totalCount: surveyResults.totalCount }));
      yield put(
        actions.gettingIndividualSurveyResultsSucceeded({
          surveyResults: surveyResults.data
        })
      );
    }
  } catch (ex) {
    yield put(actions.updateSurveyResultsDetails({}))
    yield put(actions.updateSurveyHashFilter([]))
    yield put(actions.gettingIndividualSurveyResultsFailed(ex));
  }
}

export function* getAssociatedCampaignsIfNeeded(action) {
  const { surveyHash } = action.payload || {};
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let campaignUuidList;
    let campaignList = yield select(associatedCampaignList);

    if (action.type === actions.GET_ASSOCIATED_CAMPAIGNS) {
      yield put(actions.gettingAssociatedCampaigns());
      campaignUuidList = yield call(
        getAssociatedCampaignUuidAsync,
        tenantId,
        businessContextId,
        surveyHash
      );
      campaignList = yield call(
        getAssociatedCampaignDetailsAsync,
        tenantId,
        businessContextId,
        campaignUuidList
      );
    }
    yield put(actions.gettingAssociatedCampaignsSucceeded(campaignList));
  } catch (ex) {
    yield put(actions.gettingAssociatedCampaignsFailed(ex));
  }
}

export function* getGuestDetailsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let details = yield select(guestDetails);
    let eventId = payload.eventId;
    let profileUuid = payload.profileUuid;
    let subscriberListId = payload.subscriberListId;

    if (action.type === actions.GET_GUEST_DETAILS) {
      yield put(actions.gettingGuestDetails());
      if (profileUuid) {
        details = yield call(
          getProfileDetailsAsync,
          tenantId,
          businessContextId,
          profileUuid,
          subscriberListId
        )
        yield put(
          actions.gettingGuestDetailsSucceeded({ ...details })
        );
      } else {
        details = yield call(
          getGuestDetailsAsync,
          tenantId,
          businessContextId,
          eventId
        );
        yield put(
          actions.gettingGuestDetailsSucceeded(details.eventIdentifierValues)
        );
      }
    }
  } catch (ex) {
    yield put(actions.gettingGuestDetailsFailed(ex));
  }
}

export function* getSurveyLogoURLIfNeeded(action) {
  const { survey } = action.payload;
  let tenantId = survey.tenantId;
  let businessContextId = survey.businessContextId;
  if (survey.surveyTheme.imageId) {
    yield put(
      actions.gettingSurveyLogoUrlSucceeded({
        url: `${getContentServiceApiRoot(tenantId, businessContextId)}/images/${
          survey.surveyTheme.imageId
        }`
      })
    );
  }
}
export function* saveSurveyLogoIfNeeded(action) {
  try {
    // yield put(actions.gettingSurveyResults());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let survey = yield select(currentSurvey);
    let savedFile;
    let formData = new FormData();

    const { file } = survey;

    // Try to generate a unique name based on filename and date (required for Content Service)
    let name = (file && file.name && file.name.replace(/\W/g, '')) || 'image';
    name += Date.now();

    let metadataFile = new Blob(
      [JSON.stringify({ contentType: 'IMAGE', name: name })],
      { type: 'application/json' }
    );

    formData.append('file', file);
    formData.append('metadata', metadataFile);

    savedFile = yield call(
      saveSurveyLogoAsync,
      tenantId,
      businessContextId,
      formData
    );
    yield put(actions.savingSurveyLogoSucceeded({ savedFile }));
    return savedFile;
  } catch (ex) {
    yield put(actions.savingSurveyLogoFailed(ex));
  }
}

export function* sendResponseForSurveyResult(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);

    let template = produce(payload, draftTemplate => {
      draftTemplate.content = draftTemplate.content
        ? Base64.encode(JSON.stringify(draftTemplate.content))
        : '';
    });
    let response = yield call(
      sendResponseAsync,
      tenantId,
      businessContextId,
      template
    );
    yield put(actions.sendSurveyResultResponseSucceeded({ response }));
  } catch (ex) {
    yield put(actions.sendSurveyResultResponseFailed(ex));
  }
}

export function* getSurveyResultCsvFile(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let acknowledged = yield select(individualResulFilterTypes);
    let campaignUuid = yield select(individualResulFilterTags);
    let startDate = yield select(individualResulFilterStartDate);
    let endDate = yield select(individualResulFilterEndDate);
    let sort = yield select(individualResultSortBy);
    let surveyQuestions = yield select(filterSurveyQuestions);
    let filteredEventId = yield select(eventId);
    if (acknowledged.length == 0 || acknowledged.length == 2) {
      acknowledged = undefined;
    } else if (acknowledged[0] == 'ACKNOWLEDGED') {
      acknowledged = true;
    } else {
      acknowledged = false;
    }
    let bodyParams = {
      surveyHash: action.payload,
      ...(sort != '' && {
        sortBy: yield select(individualResultSortBy),
        sortOrder: yield select(individualResultSortOrder)
      }),
      ...(typeof acknowledged === 'boolean' && { acknowledged: acknowledged }),
      ...(campaignUuid.length > 0 && { campaignUuid: campaignUuid }),
      ...(Object.keys(getSurveyResponsePayload(surveyQuestions)).length > 0 && { surveyResponses: getSurveyResponsePayload(surveyQuestions) }),
      ...(startDate && moment(startDate).startOf('day').isSameOrBefore(moment().startOf('day')) && { startDate: startDate }),
      ...(endDate && moment(endDate).startOf('day').isSameOrBefore(moment().startOf('day'))
      && (startDate ? moment(endDate).startOf('day').isSameOrAfter(moment(startDate).startOf('day')) : true) && { endDate: endDate }),
      ...(filteredEventId && { eventId: filteredEventId })
    };
    let response = yield call(getSurveyResultsCsvFileAsync, tenantId, businessContextId, bodyParams);
    yield put(actions.gettingSurveyResultCsvFileSucceeded(response));
  } catch (ex) {
    yield put(actions.gettingSurveyResultCsvFileFailed(ex));
  }
}